import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { Region, Sport } from '@cds/betting-offer/tags';
import { VirtualCompetitionPrefix } from '@frontend/sports/common/core/data-access/sport-model';
import { interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { TabbedGridLink, TabbedGridLinkTarget } from '@frontend/sports/types/components/widget/types';

import { RedirectHelperService } from '../../navigation-core/redirect-helper.service';
import { SportUrlParam, UrlHelperService } from '../../navigation-core/url-helper.service';

export interface NavigationInfo {
    link?: string;
    title?: string;
    linkCount?: number;
}

@Injectable({ providedIn: 'root' })
export class WidgetNavigationService {
    constructor(
        private urlHelper: UrlHelperService,
        private redirectService: RedirectHelperService,
    ) {}

    getNavigationInfo(param: UrlParam, linkConfig?: TabbedGridLink, fixture?: Fixture): NavigationInfo {
        const navigationInfo: NavigationInfo = {};
        switch (linkConfig?.target) {
            case TabbedGridLinkTarget.SportLive:
                navigationInfo.link = this.urlHelper.getLiveSportUrl({ sport: param, isEsport: fixture?.sport.isEsport });
                break;
            case TabbedGridLinkTarget.SportLobby:
                navigationInfo.link = this.urlHelper.getSportUrl(param);
                break;
            case TabbedGridLinkTarget.CompetitionLobby:
                navigationInfo.link = this.getCompetitionData(fixture);
                break;
            default:
                navigationInfo.link = linkConfig?.linkOverride;
                break;
        }

        navigationInfo.title = interpolateSingleBracesString(
            linkConfig?.title,
            {
                sport: param.name,
                competition: param.name,
            },
            false,
        );

        return navigationInfo;
    }

    getCompetitionData(firstFixture?: Fixture): string | undefined {
        const isVirtual = !!firstFixture?.virtualCompetition;
        const competitionId = isVirtual
            ? `${VirtualCompetitionPrefix}${firstFixture?.virtualCompetition.id}`
            : (firstFixture?.competition.parentLeagueId ?? firstFixture?.competition.id);

        return firstFixture && competitionId
            ? this.getCompetitionURL(competitionId.toString(), firstFixture.competition.name.value, firstFixture.sport, firstFixture.region)
            : undefined;
    }

    getCompetitionURL(competitionId: string, competitionName: string, sport: Sport, region: Region): string {
        const sportDetail = {
            id: sport.id,
            name: sport.name.value,
        };
        const regionDetail = {
            id: region.id,
            name: region.name.value,
        };
        let competitionDetail: UrlParam | undefined;
        if (competitionId) {
            competitionDetail = {
                id: competitionId,
                name: competitionName,
            };
        }
        const league: UrlParam = {
            id: competitionId,
            name: competitionName,
        };
        const regionOrCompetition = regionDetail || competitionDetail;

        return this.urlHelper.getSportUrl(sportDetail, SportUrlParam.Betting, regionOrCompetition, league);
    }

    headerNavigationHandler(headerChevronLink: string | undefined): void {
        if (headerChevronLink) {
            this.redirectService.goToPage(headerChevronLink);
        }
    }
}
